import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Dialog,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { useSpotlightRoutes } from './SpotlightProvider';

const Kbd = styled('kbd')(({ theme }) => ({
  padding: theme.spacing(0.125, 0.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  fontSize: theme.typography.caption.fontSize,
}));

export default function Spotlight() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const history = useHistory();
  const getRoutes = useSpotlightRoutes();

  const results = useMemo(() => {
    const allRoutes = getRoutes();
    if (!deferredQuery.trim()) return allRoutes;

    const terms = deferredQuery.toLowerCase().split(/\s+/).filter(Boolean);
    return allRoutes.filter((route) => {
      const searchText = `${route.label} ${route.path} ${route.section}`.toLowerCase();
      return terms.every((term) => searchText.includes(term));
    });
  }, [deferredQuery, getRoutes]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  const navigateTo = useCallback(
    (path: string) => {
      handleClose();
      history.push(path);
    },
    [handleClose, history],
  );

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setOpen((prev) => {
          if (prev) {
            setQuery('');
            setSelectedIndex(0);
            return false;
          }
          return true;
        });
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [deferredQuery]);

  useEffect(() => {
    if (!listRef.current) return;
    const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          event.preventDefault();
          if (results.length > 0) {
            navigateTo(results[selectedIndex].path);
          }
          break;
      }
    },
    [results, selectedIndex, navigateTo],
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            position: 'fixed',
            top: '15%',
            m: 0,
            maxHeight: '60vh',
            borderRadius: 2,
          },
        },
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.3)' },
        },
      }}
    >
      <TextField
        autoFocus
        fullWidth
        placeholder="Search pages..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleInputKeyDown}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': { border: 'none' },
          },
          borderBottom: 1,
          borderColor: 'divider',
        }}
      />

      <List ref={listRef} sx={{ overflow: 'auto', py: 0.5 }}>
        {results.length === 0 && (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">No pages found</Typography>
          </Box>
        )}

        {results.map((route, index) => (
          <ListItem key={route.path} disablePadding data-index={index}>
            <ListItemButton
              selected={index === selectedIndex}
              onClick={() => navigateTo(route.path)}
              sx={{ py: 1, px: 2 }}
            >
              <ListItemText
                primary={route.label}
                secondary={route.path}
                slotProps={{
                  secondary: {
                    sx: { fontSize: '0.75rem', color: 'text.disabled' },
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                {route.section}
              </Typography>
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          <Kbd>↑↓</Kbd> navigate
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <Kbd>↵</Kbd> open
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <Kbd>esc</Kbd> close
        </Typography>
      </Box>
    </Dialog>
  );
}
